/**
 * BreedSelection.jsx
 * MbuziPlus — Topic 1: Breed Selection
 *
 * Changes from v1:
 *  • Progress (completed lessons), quiz answers, and journal all persist
 *    to Firestore: imfuyo_academy/{token}/mbuziplus_course/{token}
 *    merged into the same doc the onboarding gate created.
 *  • Lesson insight blocks redesigned — field-notebook style with a
 *    left green rule, no card borders, generous whitespace.
 *  • Quiz saves per-question answers and reveals to Firestore on each tap.
 *  • Journal autosaves to Firestore (debounced 900 ms).
 *  • Home screen shows a real filled-progress ring per lesson row.
 *  • Breed images in "Meet the Breeds" no longer crop — they size to
 *    their natural aspect ratio (object-fit: contain), same as the
 *    anatomy lesson images.
 *
 * Props:
 *   isDark   : boolean
 *   user     : { token: string, fullName: string, phone: string, programmes: string[] }
 *   onBack   : () => void
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { optionOrder } from "./shuffle";
import {
  ChevronLeft, ChevronRight, Play, CheckCircle2, Circle,
  BookOpen, Film, HelpCircle, PenLine, Check, X, Save,
  Loader2,
} from "lucide-react";

import { db } from "../../Libs/firebase-config.mjs";
import { ModuleIntro, SourcesPanel } from "./ModuleFrame.jsx";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

/* ──────────────────────────────────────────────────────────
   ANIMATIONS & BASE CSS
   ────────────────────────────────────────────────────────── */
const css = `
  @keyframes fadeUp {
    from { opacity:0; transform:translateY(14px); }
    to   { opacity:1; transform:translateY(0); }
  }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes pop {
    0%   { transform: scale(0.92); opacity: 0; }
    100% { transform: scale(1);    opacity: 1; }
  }
  .bs-card { transition: transform 0.15s ease, border-color 0.15s ease; }
  .bs-card:hover { transform: translateY(-2px); }
  .bs-breed-img { transition: transform 0.5s cubic-bezier(0.25,0.46,0.45,0.94); }
  .bs-breed-img:hover { transform: scale(1.04); }
  .bs-option {
    transition: border-color 0.12s ease, background 0.12s ease, transform 0.08s ease;
    cursor: pointer;
  }
  .bs-option:hover { transform: translateX(2px); }
  .bs-fade { animation: fadeUp 0.35s cubic-bezier(0.16,1,0.3,1) both; }

  /* Field-notebook insight entry */
  .bs-insight {
    position: relative;
    padding: 0 0 28px 22px;
    border-left: 2px solid #3E7A32;
    margin-bottom: 0;
  }
  .bs-insight:last-child { padding-bottom: 0; }
  .bs-insight::before {
    content: '';
    position: absolute;
    left: -5px; top: 4px;
    width: 8px; height: 8px;
    border-radius: 50%;
    background: #3E7A32;
  }
  .bs-insight-dark { border-left-color: #14301A; }
  .bs-insight-dark::before { background: #14301A; }

  /* Breed image container — sizes to the image itself, no forced crop */
  .bs-breed-img-wrap {
    position: relative;
    overflow: hidden;
    display: block;
    width: 100%;
    line-height: 0; /* removes inline-image baseline gap */
  }
  .bs-breed-img-wrap img {
    display: block;
    width: 100%;
    height: auto;
    max-height: 70vh;      /* guards against extreme portrait images on small screens */
    object-fit: contain;   /* never crops — always shows the full image */
  }
`;

/* ──────────────────────────────────────────────────────────
   CONTENT DATA  (unchanged from v1)
   ────────────────────────────────────────────────────────── */
const BREEDS = [
  {
    name: "German Alpine",
    img: "https://res.cloudinary.com/dpymwa41m/image/upload/v1781256273/germanalpine_ptykze.jpg",
    tag: "Versatile and resilient",
    blurb: "A brown and black dairy goat that is common in Kenya and is the breed MbuziPlus recommends for most farms.",
    bullets: [
      "Colour is brown and black, with a black line down the back",
      "Does weigh about 60 kg and bucks about 65 kg",
      "Gives about 2 to 4 litres of milk a day on average (Kenya Agricultural and Livestock Research Organization)",
    ],
  },
  {
    name: "Saanen",
    img: "https://res.cloudinary.com/dpymwa41m/image/upload/v1781256273/saanen_j4zpwt.jpg",
    tag: "Volume specialist",
    blurb: "The highest-yielding dairy goat of the group, but it asks the most of you in feed, shade and hygiene.",
    bullets: [
      "White to pale cream, with pink skin",
      "Does weigh 50 to 65 kg and bucks 75 to 80 kg",
      "Gives about 2 to 5 litres of milk a day on average (Kenya Agricultural and Livestock Research Organization)",
    ],
  },
  {
    name: "Toggenburg",
    img: "https://res.cloudinary.com/dpymwa41m/image/upload/v1781256273/Toggenburggoat_feature_nejfrf.jpg",
    tag: "Steady and sturdy",
    blurb: "A fawn to chocolate brown goat with white ears, legs and two white stripes on the face.",
    bullets: [
      "Does weigh 50 to 65 kg and bucks 55 to 80 kg",
      "Gives about 2 to 4 litres of milk a day on average (Kenya Agricultural and Livestock Research Organization)",
      "Needs good forage and enough space for its body size",
    ],
  },
  {
    name: "Local goats such as the Galla",
    img: "https://res.cloudinary.com/dpymwa41m/image/upload/v1781256273/galagoats_n32eeg.jpg",
    tag: "The hardy local parent",
    blurb: "Local goats give little milk, but they are used in crosses because they cope with local heat, feed and parasites.",
    bullets: [
      "Not a dairy breed, so milk yield is low",
      "Used as the local parent when breeding crossbred dairy goats",
      "Hardiness is the reason they are crossed with dairy breeds",
    ],
  },
];

const VIDEO_URL    = "https://res.cloudinary.com/dpymwa41m/video/upload/v1781255451/Kenya_Dairy_Goats_idlbg8.mp4";
const VIDEO_POSTER = BREEDS[0].img;

const LESSONS = {
  "why-alpine": {
    title: "Why MbuziPlus Recommends the German Alpine",
    intro: "No breed is best everywhere. MbuziPlus recommends the German Alpine because it gives good milk, is common in Kenya, and is easy to find with a known record.",
    items: [
      { h: "What the programme looks for", p: "A smallholder needs three things from a goat at the same time: milk you can sell, a goat that survives your climate, and animals you can find and verify. The German Alpine is common in Kenya, so buyers and sellers know it and its breeding records are easier to check." },
      { h: "How it compares in Kenya", p: "The Kenya Agricultural and Livestock Research Organization lists the average daily milk of the Saanen at 2 to 5 litres, and of the German or French Alpine and the Toggenburg at 2 to 4 litres. The Alpine gives up a little peak yield to the Saanen, but the Saanen needs more careful care." },
      { h: "Size matters for feed", p: "A German Alpine doe weighs about 60 kg. A Saanen doe weighs 50 to 65 kg. A goat that eats more needs more feed, so a bigger goat only pays if you can feed it." },
      { h: "The trade-off to remember", p: "Pure imported breeds can give more milk, but they usually need better feed, shade and health care. Your choice is a balance between the milk you hope for and the care you can really give every day." },
      { h: "How MbuziPlus helps here", p: "Under MbuziPlus, each goat is verified and its breed and records are kept in one record. That means you start with a goat whose breed you can trust, not just a seller's word." },
      { h: "Check yourself", p: "Question: Why does a bigger goat that gives more milk not always make more money? Answer: Because it eats more and needs better care. The extra milk only pays if you can meet those costs." },
    ],
    sources: ["Kenya Agricultural and Livestock Research Organization, Dairy Goat Breeds and Breeding"],
  },
  "milk-timeline": {
    title: "The Milk Timeline: When, How Much, For How Long",
    intro: "A doe gives milk only after she has kidded. To plan your income you need to know when milk starts, when it peaks, how long it lasts and when she rests.",
    items: [
      { h: "Milk begins after kidding", p: "A doe does not give milk until she has her first kids. Milk production is called lactation. Her first lactation usually gives less milk than later ones, so do not judge a young doe by her first round." },
      { h: "When does the milk peak?", p: "After kidding, the daily yield rises and reaches its highest point about 4 to 6 weeks after kidding (Washington State University). After the peak, the yield slowly falls." },
      { h: "How long does one lactation last?", p: "In United States herds that record milk, the average lactation is about 284 days, or just over 9 months. In a smallholder Kenyan herd with limited feed, many does will give milk for less time. Your own records will show your goats' real length." },
      { h: "How much milk to expect", p: "In the United States, Alpine and Saanen does on milk recording average about 1,230 kilograms of milk per lactation, which is about 4.3 kilograms a day averaged over 284 days. In Kenya, the Kenya Agricultural and Livestock Research Organization gives 2 to 4 litres a day for the Alpine. Use the Kenyan figure for planning, because the American herds are fed and managed very closely." },
      { h: "The dry period", p: "Before the next kidding the doe is allowed a rest and is not milked. Dairy farmers give this rest so the doe can rebuild her body for the next lactation. Ask your veterinary officer how many weeks to plan for your goats." },
      { h: "Putting it on a calendar", p: "Kidding, then milk rising to a peak at 4 to 6 weeks, then slow decline, then a rest, then kidding again. A good doe repeats this cycle every year. Counting the weeks from kidding on a calendar is the first habit of a good dairy goat farmer." },
      { h: "Check yourself", p: "Question: A doe kidded 5 weeks ago and her milk is higher than last week. Is that normal? Answer: Yes. Yield normally peaks about 4 to 6 weeks after kidding." },
    ],
    sources: ["Washington State University Extension, Dairy Goat Production", "Kenya Agricultural and Livestock Research Organization, Dairy Goat Breeds and Breeding"],
  },
  "age-question": {
    title: "The Age Question: Why MbuziPlus Starts With Young Goats",
    intro: "How old a goat is when you buy her changes how well she settles, how soon she earns, and how much risk you carry.",
    items: [
      { h: "The growth clock", p: "Young does reach sexual maturity at about 4 to 5 months. Washington State University advises breeding does when they weigh about 70 to 80 pounds, which is about 32 to 36 kilograms. This usually happens at 7 to 10 months of age. A doe that is bred too small or too young struggles with pregnancy and growth." },
      { h: "From buying to first milk", p: "A doe bred at about 8 months carries her kids for about 149 days, which is just under 5 months. So her first kids and first milk come at roughly 13 months of age. A goat bought at 5 to 7 months therefore needs only a few months on your farm before breeding." },
      { h: "Why MbuziPlus favours young goats", p: "A young goat is cheaper than an adult in milk. She has time to adjust to your feed, your climate and your local parasites before the stress of pregnancy and milking begins. This is MbuziPlus's programme advice, and your Imfuyo field officer can adapt it to your farm." },
      { h: "Why not buy an adult in milk?", p: "An adult in milk costs more and you cannot be sure of her health history. She may also carry diseases and parasites that are new to your farm. If you buy one, keep her apart from your herd for 30 days first (Washington State University)." },
      { h: "The cost of waiting", p: "A young goat gives no milk for several months. Plan your cash for that gap, so you are not tempted to sell her early. The Planner and the financial modules later in this course help you do this." },
      { h: "Check yourself", p: "Question: At about what weight and age is a young doe usually bred? Answer: About 32 to 36 kilograms, usually at 7 to 10 months old." },
    ],
    sources: ["Washington State University Extension, Dairy Goat Production"],
  },
  "heat-breeding": {
    title: "Heat and Breeding: Getting the Timing Right",
    intro: "A doe can only become pregnant for a short time each cycle. If you miss it, you wait about three weeks for another chance.",
    items: [
      { h: "The cycle", p: "A doe comes on heat about every 18 to 22 days. Each heat lasts 12 to 48 hours, with an average of 36 hours. She releases her eggs 24 to 36 hours after the heat begins (Kenya Agricultural and Livestock Research Organization)." },
      { h: "Signs of heat", p: "Look for restlessness, a red and swollen vulva, a mucus discharge, a wagging tail, more bleating, head butting and frequent urine. Standing to be mounted by other goats is not a reliable sign in goats." },
      { h: "Using a buck to help", p: "A buck in a neighbouring pen can bring out clear signs of heat. Keep bucks in a separate pen from the does until you want to breed, so that you know the breeding date." },
      { h: "When to breed", p: "The Kenya Agricultural and Livestock Research Organization advises breeding the doe 24 hours after the heat begins, or every 12 hours until the heat ends. Write down the date. It is the start of your kidding calendar." },
      { h: "Why record the date", p: "Gestation lasts 145 to 155 days, an average of 149 (Washington State University). With the breeding date written down, you know when to prepare the kidding pen, and you know if the doe has not become pregnant when she returns to heat." },
      { h: "Check yourself", p: "Question: A doe shows heat on Monday morning. When should you breed her? Answer: About 24 hours after heat begins, so Tuesday morning, or every 12 hours until the heat ends." },
    ],
    sources: ["Kenya Agricultural and Livestock Research Organization, Dairy Goat Breeds and Breeding", "Washington State University Extension, Dairy Goat Production"],
  },
  "gestation-kidding": {
    title: "Pregnancy and Kidding: What to Plan For",
    intro: "Kidding is the day your investment starts to pay. A little planning protects the doe and the kids.",
    items: [
      { h: "How long is pregnancy?", p: "Gestation lasts 145 to 155 days, with an average of 149 days (Washington State University). Count forward from the breeding date and mark the expected kidding week on your calendar." },
      { h: "How many kids?", p: "A doe has one to three kids per year. Twins are the usual goal. Birth weight of a single kid is about 6 to 6.5 pounds, which is 2.7 to 2.9 kilograms, and it drops when there are more kids." },
      { h: "How often can a doe kid?", p: "The Kenya Agricultural and Livestock Research Organization describes a good doe as one with fertility of up to 1.5 kiddings a year. That means three kidding seasons in two years. This only works if the doe stays in good body condition." },
      { h: "Body condition at kidding", p: "Washington State University scores body condition from 1 (very thin) to 5 (very fat). The goal is a score of 3 when the doe is late in pregnancy and about to start milking. A doe that is too thin or too fat has more trouble at kidding and gives less milk." },
      { h: "Prepare before the date", p: "Prepare a clean, dry kidding pen before the expected date. Have clean water, a warm place for the kids and your veterinary officer's number. Module 19 of this course covers kidding and kid rearing in full detail." },
      { h: "Check yourself", p: "Question: A doe is bred on 1 March. Roughly when will she kid? Answer: About 149 days later, which is around the end of July." },
    ],
    sources: ["Washington State University Extension, Dairy Goat Production", "Kenya Agricultural and Livestock Research Organization, Dairy Goat Breeds and Breeding"],
  },
  "lactation-curve": {
    title: "Reading the Lactation Curve: Your Goat's Report Card",
    intro: "A lactation curve is the line you get when you write down your goat's milk each day. Once you know the normal shape, a change tells you where to look.",
    items: [
      { h: "The normal shape", p: "Milk rises after kidding, peaks about 4 to 6 weeks later, then falls slowly (Washington State University). The line should be smooth. Weigh or measure the milk at the same time each day and write it in a notebook or in the course journal." },
      { h: "A drop in milk is a question, not an answer", p: "When milk falls faster than normal, the cause is usually something you can fix: not enough feed or water, worms, a sick udder, heat stress or a change in routine. Check these before blaming the goat." },
      { h: "Feed and water first", p: "High-producing does lose body condition early in lactation and regain it later (Washington State University). If feed is short, milk falls quickly. Check the feed and water trough before anything else." },
      { h: "Check the udder", p: "Mastitis is an infection of the udder. It cuts milk and spoils its quality. Keep the pen dry and clean, milk with clean hands and handle the teats gently. Module 21 covers milking and milk quality in detail." },
      { h: "An early or very low peak", p: "If the peak is low from the start, think about the goat's feeding in late pregnancy, her body condition at kidding, her health and her breeding. Write down what you see and discuss it with your veterinary officer or field officer." },
      { h: "Check yourself", p: "Question: A doe's milk drops sharply in month 4. What do you check first? Answer: Feed and water, then worms and the udder. Do not blame the breed first." },
    ],
    sources: ["Washington State University Extension, Dairy Goat Production"],
  },
  "sweet-spot": {
    title: "Crossbreds: Balancing Milk and Survival",
    intro: "A goat that gives a lot of milk but dies young earns nothing. Crossbreeding tries to combine milk from one parent with hardiness from the other.",
    items: [
      { h: "What a crossbred is", p: "A crossbred has a dairy breed such as the German Alpine on one side and local goats on the other. You may hear 'a three-quarter Alpine'. That means about three quarters of the goat's ancestry is from the dairy breed and one quarter from local goats." },
      { h: "Why not go all the way to pure?", p: "Pure imported dairy goats usually give more milk but need better feed, shade and health care. Local goats are hardy but give little milk. A crossbred is the middle road. The more dairy blood you add, the more milk you can expect, and the more care you must give." },
      { h: "MbuziPlus's current advice", p: "MbuziPlus recommends a crossbred with a high share of dairy blood, around three quarters, for most smallholder farms. This is the programme's current advice. The right level depends on your feed, your climate and your skill, so discuss it with your field officer." },
      { h: "Why records matter", p: "You can only know a goat's breed share if someone recorded her parents. This is why MbuziPlus verifies each goat and keeps her record. A seller's word is not a record." },
      { h: "Questions to ask a seller", p: "What are her parents? What is her breed share? How much milk did her mother give? What does she eat now? Has she been vaccinated and dewormed? Write the answers down." },
      { h: "Check yourself", p: "Question: A seller offers a 'pure' goat at a high price. What do you ask before you pay? Answer: How will I feed and care for her, what is her written record, and can I meet the care she needs." },
    ],
    sources: ["Kenya Agricultural and Livestock Research Organization, Dairy Goat Breeds and Breeding"],
  },
  "buyer-checklist": {
    title: "Choosing a Doe: A Buyer's Checklist",
    intro: "Before money changes hands, look at the goat with the eyes of a buyer. These points come from the national research organization's guide to selecting breeding does.",
    items: [
      { h: "True to type", p: "She should look like her breed: the colour, shape and size of an Alpine, Saanen or Toggenburg, as described in lesson 1. A goat that looks nothing like her breed may be a poor mix." },
      { h: "Strong legs and feet", p: "A doe walks, grazes and stands to be milked every day. Weak or crooked legs are a reason to cull (Kenya Agricultural and Livestock Research Organization)." },
      { h: "Body condition and temperament", p: "She should keep good body condition, be a good mother and be calm to handle. A nervous goat is hard to milk." },
      { h: "The udder", p: "The Kenya Agricultural and Livestock Research Organization asks for a prominent, deep, well-attached udder with well-placed, long teats. A hanging udder or very short teats make milking hard and cause injuries." },
      { h: "Fertility", p: "Ask how often she has kidded. Fertility of up to 1.5 kiddings a year is the aim." },
      { h: "Health and quarantine", p: "Washington State University advises keeping new animals apart for 30 days before joining your herd. Ask a veterinarian to screen purchases for diseases such as caseous lymphadenitis (CL), caprine arthritis encephalitis (CAE), Johne's disease and sore mouth." },
      { h: "Reasons to cull", p: "The Kenya Agricultural and Livestock Research Organization lists reasons to remove goats from the flock: physical defects, fertility problems, health problems such as mastitis and pneumonia, udder problems, low milk, slow growth, old age and avoiding inbreeding." },
      { h: "Check yourself", p: "Question: You have just bought a doe. What do you do before she joins your herd? Answer: Keep her apart for 30 days and have her checked by a veterinarian." },
    ],
    sources: ["Kenya Agricultural and Livestock Research Organization, Dairy Goat Breeds and Breeding", "Washington State University Extension, Dairy Goat Production"],
  },
  "health-check": {
    title: "Health Reality Check: Your Ceiling and Your Floor",
    intro: "The breed you pick sets the highest milk your goat could give. Daily health care decides how close she gets.",
    items: [
      { h: "The ceiling and the floor", p: "Think of breed as the ceiling and management as the floor. A good breed with poor feed, dirty housing and no health care gives poor results. Most farms lose more milk to management problems than to the wrong breed." },
      { h: "Mastitis", p: "Washington State University lists the main ways to prevent mastitis: sanitary conditions, good milking procedures, ventilated housing and dry bedding. Teat-end and udder injuries also let infection in." },
      { h: "Body condition", p: "Score each doe from 1 to 5 every month. A score of 3 at the end of pregnancy gives the best start to lactation. Learn to feel her backbone and ribs, not only to look at her." },
      { h: "Parasites and vaccination", p: "Worms and coccidiosis are common in kids and young goats and can seriously reduce growth and survival. Vaccination and deworming calendars are set with your veterinary officer, because they depend on your area. Modules 12 and 13 cover health and diseases in full." },
      { h: "Keep records", p: "Write down dates of heat, breeding, kidding, deworming, vaccination and illness for every goat. Records turn a guess into a decision." },
      { h: "Check yourself", p: "Question: What score on the 1 to 5 body condition scale do you aim for in late pregnancy? Answer: A score of 3." },
    ],
    sources: ["Washington State University Extension, Dairy Goat Production"],
  },
  "worked-example": {
    title: "Worked Example: Wanjiru Plans Her First Two Does",
    intro: "This is an example with simple numbers, so you can see how the lessons fit together. The numbers are for practice and are not a promise of what you will earn.",
    items: [
      { h: "The farm", p: "Wanjiru has a small plot in the highlands, with Napier grass, a shed and water nearby. She wants two dairy does and is deciding what to buy." },
      { h: "Step 1: Choose the breed", p: "She compares the Kenyan figures: Alpine 2 to 4 litres a day, Saanen 2 to 5 litres. The Saanen could give more, but she knows her feed is limited. She chooses the German Alpine, as MbuziPlus recommends, because it is easier to feed and find verified." },
      { h: "Step 2: Choose the age", p: "She buys two verified does aged 6 months. They arrive at about 25 kilograms, so she plans to breed them when they reach about 32 to 36 kilograms, usually at 7 to 10 months." },
      { h: "Step 3: Plan the calendar", p: "She breeds one doe on 1 March. Gestation is about 149 days, so she expects kids around 28 July. Milk peaks about 4 to 6 weeks later, in late August to mid September." },
      { h: "Step 4: Estimate the milk", p: "If a doe gives 2 litres a day for 7 months, which is about 210 days, she gives about 420 litres. At 3 litres a day she gives about 630 litres. Wanjiru multiplies each figure by the milk price near her, and subtracts feed and health costs. The financial modules later in this course show how to do this properly." },
      { h: "Step 5: Keep records", p: "She writes down the breeding date, kidding date, daily milk, body condition, deworming and vaccination for each doe. After a year she can see which doe earns and which should be culled." },
      { h: "Check yourself", p: "Question: A doe gives 2.5 litres a day for 200 days. How much milk is that? Answer: 2.5 times 200 equals 500 litres." },
    ],
    sources: ["Kenya Agricultural and Livestock Research Organization, Dairy Goat Breeds and Breeding", "Washington State University Extension, Dairy Goat Production"],
  },
};

const LESSON_ORDER = [
  { id: "breeds",            type: "breeds",  title: "Meet the Breeds",                                  duration: "20 min",        icon: BookOpen  },
  { id: "why-alpine",        type: "lesson",  title: "Why MbuziPlus Recommends the German Alpine",       duration: "20 min",        icon: BookOpen  },
  { id: "milk-timeline",     type: "lesson",  title: "The Milk Timeline",                                duration: "25 min",        icon: BookOpen  },
  { id: "age-question",      type: "lesson",  title: "The Age Question: Starting With Young Goats",      duration: "20 min",        icon: BookOpen  },
  { id: "heat-breeding",     type: "lesson",  title: "Heat and Breeding: Getting the Timing Right",      duration: "25 min",        icon: BookOpen  },
  { id: "gestation-kidding", type: "lesson",  title: "Pregnancy and Kidding: What to Plan For",          duration: "20 min",        icon: BookOpen  },
  { id: "lactation-curve",   type: "lesson",  title: "Reading the Lactation Curve",                      duration: "25 min",        icon: BookOpen  },
  { id: "sweet-spot",        type: "lesson",  title: "Crossbreds: Balancing Milk and Survival",          duration: "20 min",        icon: BookOpen  },
  { id: "buyer-checklist",   type: "lesson",  title: "Choosing a Doe: A Buyer's Checklist",              duration: "25 min",        icon: BookOpen  },
  { id: "health-check",      type: "lesson",  title: "Health Reality Check",                             duration: "20 min",        icon: BookOpen  },
  { id: "worked-example",    type: "lesson",  title: "Worked Example: Wanjiru Plans Her First Two Does", duration: "25 min",        icon: BookOpen  },
  { id: "video",             type: "video",   title: "Watch: Kenya Dairy Goats, Full Explainer",         duration: "Watch anytime", icon: Film      },
  { id: "quiz",              type: "quiz",    title: "Check Your Understanding",                         duration: "10 questions",  icon: HelpCircle },
  { id: "journal",           type: "journal", title: "Your Breed Selection Plan",                        duration: "Write and save", icon: PenLine   },
];

export const QUIZ = [
  { q: "According to the Kenya Agricultural and Livestock Research Organization, which breed has the highest range of average daily milk in Kenya?", options: ["Toggenburg, 2 to 4 litres", "Saanen, 2 to 5 litres", "German Alpine, 2 to 3 litres", "Local goats, 3 to 6 litres"], correct: 1, why: "The Kenya Agricultural and Livestock Research Organization gives the Saanen an average of 2 to 5 litres a day. The Alpine and Toggenburg are 2 to 4 litres." },
  { q: "Why does a heavier, higher-yielding goat not always earn more?", options: ["Heavier goats never give milk", "She eats more and needs better care, so the extra milk only pays if you can provide it", "Heavier goats are always sick", "Milk price falls with goat size"], correct: 1, why: "More milk needs more feed and better care. If you cannot supply it, the extra yield is lost." },
  { q: "About when does a doe's daily milk usually peak after kidding?", options: ["In the first 2 days", "About 4 to 6 weeks after kidding", "At 5 months", "Just before dry-off"], correct: 1, why: "Washington State University gives the peak at 4 to 6 weeks after kidding." },
  { q: "A doe's heat begins on Monday morning. When do you breed her?", options: ["Straight away on Monday", "About 24 hours after the heat begins, or every 12 hours until it ends", "After one week", "Any time next month"], correct: 1, why: "The Kenya Agricultural and Livestock Research Organization advises breeding 24 hours after heat begins, or every 12 hours until the heat ends." },
  { q: "About how long is a goat's pregnancy?", options: ["About 90 days", "About 149 days", "About 200 days", "About 12 months"], correct: 1, why: "Gestation is 145 to 155 days, an average of 149 days." },
  { q: "You bought a new doe. What should you do before she joins your herd?", options: ["Put her straight in with the herd to settle", "Keep her apart for 30 days and have her checked by a veterinarian", "Milk her heavily to test her", "Only check her colour"], correct: 1, why: "A 30-day separation and veterinary screening protect your herd from diseases and parasites." },
  { q: "Which is NOT on the Kenya Agricultural and Livestock Research Organization's list of desirable traits in a breeding doe?", options: ["Strong legs and feet", "A prominent, deep, well-attached udder", "Good mothering and temperament", "A very small udder with short teats"], correct: 3, why: "The Kenya Agricultural and Livestock Research Organization asks for well-placed, long teats and a deep, well-attached udder, not a very small udder." },
  { q: "A doe's milk drops sharply in month 4. What do you check first?", options: ["Assume the breed is poor and sell her", "Feed and water, then worms and the udder", "Wait for it to recover by itself", "Change her name"], correct: 1, why: "Most drops come from feed, water, worms or udder problems, which you can fix." },
  { q: "What body condition score do you aim for in late pregnancy, on the 1 to 5 scale?", options: ["1", "2", "3", "5"], correct: 2, why: "Washington State University gives a score of 3 as the goal when does enter lactation." },
  { q: "In your own words: why can management matter more than breed?", options: ["It cannot, breed decides everything", "Breed sets the ceiling, but feed, hygiene and health decide how close she gets to it", "Management only matters for sick goats", "Because records are not useful"], correct: 1, why: "A good breed with poor care gives poor results, while a decent goat with good care performs well." },
];

/* ──────────────────────────────────────────────────────────
   FIRESTORE HELPERS
   Keyed by the account's unique access token — imfuyo_academy/{token}
   — NOT by phone number. Phone numbers can repeat, change format,
   or be missing; the token is the stable, unique doc ID used
   everywhere else in the app (onboarding gate, admin dashboard).
   ────────────────────────────────────────────────────────── */
const TOPIC_ID = "breed-selection";

function profileRef(u) {
  // u is the signed-in user; group members keep their own progress under pid.
  return doc(db, "imfuyo_academy", u.token, "mbuziplus_course", u.pid || u.token);
}

async function loadProgress(token) {
  try {
    const snap = await getDoc(profileRef(token));
    if (!snap.exists()) return {};
    const data = snap.data();
    return data[TOPIC_ID] || {};
  } catch (err) {
    console.error("loadProgress:", err);
    return {};
  }
}

async function saveProgress(token, patch) {
  try {
    await setDoc(
      profileRef(token),
      { [TOPIC_ID]: patch, updatedAt: serverTimestamp() },
      { merge: true }
    );
  } catch (err) {
    console.error("saveProgress:", err);
  }
}

/* ──────────────────────────────────────────────────────────
   SMALL HELPERS
   ────────────────────────────────────────────────────────── */
function useIsMobile() {
  const [mobile, setMobile] = useState(() => window.innerWidth < 640);
  useEffect(() => {
    const fn = () => setMobile(window.innerWidth < 640);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return mobile;
}

/* Field-notebook insight block — left-rule style */
function InsightBlock({ item, isDark, index = 0 }) {
  const ruleColor   = isDark ? "#14301A" : "#3E7A32";
  const dotColor    = isDark ? "#14301A" : "#3E7A32";
  const labelColor  = isDark ? "#8DBB7E" : "#285020";
  const bodyColor   = isDark ? "#c8e0cc" : "#2d3e31";

  return (
    <div
      className="bs-fade"
      style={{
        position: "relative",
        paddingLeft: "20px",
        paddingBottom: index === -1 ? 0 : "28px", /* caller sets -1 for last */
        borderLeft: `2px solid ${ruleColor}`,
        animationDelay: `${index * 0.05}s`,
      }}
    >
      {/* dot on the rule */}
      <div style={{
        position: "absolute",
        left: "-5px", top: "5px",
        width: "8px", height: "8px",
        borderRadius: "50%",
        background: dotColor,
      }} />

      {/* heading */}
      <p style={{
        margin: "0 0 5px",
        fontSize: "11px",
        fontWeight: 800,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: labelColor,
        lineHeight: 1.4,
      }}>
        {item.h}
      </p>

      {/* body */}
      <p style={{
        margin: 0,
        fontSize: "15px",
        lineHeight: 1.75,
        color: bodyColor,
        fontFamily: "Georgia, 'Times New Roman', serif",
        fontWeight: 400,
      }}>
        {item.p}
      </p>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────
   MAIN COMPONENT
   ────────────────────────────────────────────────────────── */
export default function BreedSelection({ isDark = false, user, onBack }) {
  const isMobile = useIsMobile();

  /* ── local state ── */
  const [view,         setView]         = useState("home");
  const [loading,      setLoading]      = useState(true);

  /* persisted state — keyed by lesson id */
  const [completed,    setCompleted]    = useState({});   // { lessonId: true }
  const [answers,      setAnswers]      = useState({});   // { qIndex: optionIndex }
  const [revealed,     setRevealed]     = useState({});   // { qIndex: true }
  const [journal,      setJournal]      = useState("");
  const [journalSave,  setJournalSave]  = useState("idle"); // idle|saving|saved|error

  const saveTimer = useRef(null);
  const token     = user?.token ? user : null;

  /* ── colours ── */
  const bg       = isDark ? "#0d1a0f" : "#f5f9f5";
  const cardBg   = isDark ? "#13231a" : "#ffffff";
  const border   = isDark ? "#223328" : "#e2efe2";
  const textMain = isDark ? "#f0f7f0" : "#0d1a0f";
  const textSub  = isDark ? "#7fa183" : "#5a7a5d";
  const accent   = "#3E7A32";

  /* ── load saved progress on mount ── */
  useEffect(() => {
    if (!token) { setLoading(false); return; }
    loadProgress(token).then((saved) => {
      if (saved.completed)  setCompleted(saved.completed);
      if (saved.answers)    setAnswers(saved.answers);
      if (saved.revealed)   setRevealed(saved.revealed);
      if (typeof saved.journal === "string") setJournal(saved.journal);
      setLoading(false);
    });
  }, [token]);

  /* ── generic persist helper ── */
  const persist = useCallback((patch) => {
    if (!token) return;
    saveProgress(token, patch);
  }, [token]);

  /* ── mark a lesson done ── */
  function markDone(id) {
    setCompleted((prev) => {
      if (prev[id]) return prev;
      const next = { ...prev, [id]: true };
      persist({ completed: next });
      return next;
    });
  }

  function openLesson(id) {
    setView(id);
    markDone(id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function goNext() {
    const idx = LESSON_ORDER.findIndex((l) => l.id === view);
    if (idx >= 0 && idx < LESSON_ORDER.length - 1) {
      openLesson(LESSON_ORDER[idx + 1].id);
    } else {
      setView("home");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  /* ── quiz: choose answer ── */
  function choose(qi, oi) {
    if (revealed[qi]) return;
    const nextAnswers  = { ...answers,  [qi]: oi   };
    const nextRevealed = { ...revealed, [qi]: true };
    setAnswers(nextAnswers);
    setRevealed(nextRevealed);
    persist({ answers: nextAnswers, revealed: nextRevealed });
  }

  /* ── journal: debounced save ── */
  function handleJournalChange(val) {
    setJournal(val);
    setJournalSave("idle");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setJournalSave("saving");
      try {
        await saveProgress(token, { journal: val });
        setJournalSave("saved");
      } catch {
        setJournalSave("error");
      }
    }, 900);
  }

  /* ── progress numbers ── */
  const doneCount   = Object.keys(completed).length;
  const totalCount  = LESSON_ORDER.length;
  const progressPct = Math.min(100, Math.round((doneCount / totalCount) * 100));
  const quizCorrect = Object.entries(answers).filter(([qi, ai]) => QUIZ[+qi].correct === ai).length;
  const quizAnswered = Object.keys(revealed).length;

  /* ── loading screen ── */
  if (loading) {
    return (
      <div style={{
        minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
        background: bg, fontFamily: "'Inter','DM Sans',system-ui,sans-serif",
      }}>
        <style>{css}</style>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "14px" }}>
          <Loader2 size={28} color={accent} style={{ animation: "spin 1s linear infinite" }} />
          <p style={{ margin: 0, fontSize: "14px", color: textSub, fontWeight: 600 }}>Loading your progress…</p>
        </div>
      </div>
    );
  }

  /* ───────────────────────────────────────────────────────
     SHARED CHROME
     ─────────────────────────────────────────────────────── */
  const Header = ({ title, onBackClick }) => (
    <div className="bs-fade" style={{
      maxWidth: "860px", margin: "0 auto",
      padding: isMobile ? "20px 18px 16px" : "28px 32px 20px",
      display: "flex", alignItems: "center", gap: "12px",
    }}>
      <button
        onClick={onBackClick}
        style={{
          display: "inline-flex", alignItems: "center", gap: "6px",
          background: "none", border: `1px solid ${isDark ? "#243529" : "#c8e0c8"}`,
          borderRadius: "50px", padding: "7px 16px",
          fontSize: "12px", fontWeight: 600, color: textSub,
          cursor: "pointer", fontFamily: "inherit", flexShrink: 0,
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = accent; e.currentTarget.style.color = accent; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = isDark ? "#243529" : "#c8e0c8"; e.currentTarget.style.color = textSub; }}
      >
        <ChevronLeft size={13} /> {view === "home" ? "Course" : "Breed Selection"}
      </button>
      {title && (
        <h2 style={{
          margin: 0, fontSize: isMobile ? "14px" : "16px", fontWeight: 700, color: textMain,
          fontFamily: "Georgia, 'Times New Roman', serif",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {title}
        </h2>
      )}
    </div>
  );

  const NextButton = ({ label = "Continue" }) => (
    <button
      onClick={goNext}
      style={{
        display: "inline-flex", alignItems: "center", gap: "8px",
        background: accent, color: "#fff", border: "none",
        borderRadius: "14px", padding: "13px 26px",
        fontSize: "14px", fontWeight: 800, cursor: "pointer",
        fontFamily: "inherit", letterSpacing: "-0.01em",
        boxShadow: "0 4px 20px rgba(34,197,94,0.4)",
        transition: "background 0.15s",
      }}
      onMouseEnter={e => e.currentTarget.style.background = "#285020"}
      onMouseLeave={e => e.currentTarget.style.background = accent}
    >
      {label} <ChevronRight size={15} />
    </button>
  );

  /* ───────────────────────────────────────────────────────
     VIEW: HOME
     ─────────────────────────────────────────────────────── */
  if (view === "home") {
    return (
      <div style={{ minHeight: "100vh", background: bg, fontFamily: "'Inter','DM Sans',system-ui,sans-serif", paddingTop: "80px" }}>
        <style>{css}</style>
        <Header title={null} onBackClick={onBack} />

        <main style={{ maxWidth: "860px", margin: "0 auto", padding: isMobile ? "0 18px 80px" : "0 32px 80px" }}>

          <ModuleIntro moduleId="breed-selection" isDark={isDark} onStart={() => document.getElementById("mbz-lessons")?.scrollIntoView({ behavior: "smooth" })} />

          {/* Hero */}
          <div className="bs-fade" style={{ marginBottom: "32px" }}>
            <span style={{ fontSize: "11px", fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: isDark ? "#3a7040" : "#5a9060" }}>
              Module 4 of 42 · Breed Selection
            </span>
            <h1 style={{
              margin: "8px 0 8px",
              fontFamily: "Georgia, 'Times New Roman', serif",
              fontSize: isMobile ? "26px" : "34px",
              fontWeight: 700, color: textMain, lineHeight: 1.15, letterSpacing: "-0.02em",
            }}>
              Find the goat that fits your land,<br />climate, and budget
            </h1>
            <p style={{ margin: "0 0 20px", fontSize: "14px", color: textSub, lineHeight: 1.65, maxWidth: "520px" }}>
              Every lesson is built from published research and extension guides, with the sources listed at the end of each lesson.</p>

            {/* Progress bar + stats row */}
            <div style={{ display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div style={{ width: "160px", height: "5px", borderRadius: "3px", background: isDark ? "#1e3321" : "#dbeedd", overflow: "hidden" }}>
                  <div style={{ width: `${progressPct}%`, height: "100%", background: accent, borderRadius: "3px", transition: "width 0.5s ease" }} />
                </div>
                <span style={{ fontSize: "12px", color: textSub, fontWeight: 600, whiteSpace: "nowrap" }}>
                  {doneCount}/{totalCount} opened
                </span>
              </div>
              {quizAnswered > 0 && (
                <span style={{
                  fontSize: "12px", fontWeight: 700,
                  color: isDark ? "#8DBB7E" : "#285020",
                  background: isDark ? "#14301A" : "#E3EEDD",
                  padding: "3px 12px", borderRadius: "50px",
                  border: `1px solid ${isDark ? "#14301A" : "#B7D9A8"}`,
                }}>
                  Quiz: {quizCorrect}/{quizAnswered} correct
                </span>
              )}
            </div>
          </div>

          <span id="mbz-lessons" />
          {/* Lesson list */}
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {LESSON_ORDER.map((l, i) => {
              const Icon   = l.icon;
              const isDone = !!completed[l.id];

              /* extra status chips for quiz */
              let chip = null;
              if (l.id === "quiz" && quizAnswered > 0) {
                chip = (
                  <span style={{
                    fontSize: "11px", fontWeight: 700,
                    color: isDark ? "#8DBB7E" : "#285020",
                    background: isDark ? "#14301A" : "#E3EEDD",
                    padding: "2px 10px", borderRadius: "50px",
                    border: `1px solid ${isDark ? "#14301A" : "#B7D9A8"}`,
                    whiteSpace: "nowrap",
                  }}>
                    {quizCorrect}/{quizAnswered}
                  </span>
                );
              }
              if (l.id === "journal" && journal.trim().length > 0) {
                chip = (
                  <span style={{
                    fontSize: "11px", fontWeight: 700,
                    color: isDark ? "#93c5fd" : "#1d4ed8",
                    background: isDark ? "#0c1a2e" : "#eff6ff",
                    padding: "2px 10px", borderRadius: "50px",
                    border: `1px solid ${isDark ? "#1e3a5f" : "#bfdbfe"}`,
                    whiteSpace: "nowrap",
                  }}>
                    {journal.trim().split(/\s+/).filter(Boolean).length}w saved
                  </span>
                );
              }

              return (
                <div
                  key={l.id}
                  className="bs-card bs-fade"
                  onClick={() => openLesson(l.id)}
                  style={{
                    animationDelay: `${i * 0.035}s`,
                    display: "flex", alignItems: "center", gap: "14px",
                    background: cardBg,
                    border: `1px solid ${isDone ? (isDark ? "#14301A" : "#B7D9A8") : border}`,
                    borderRadius: "14px", padding: "14px 16px",
                    cursor: "pointer",
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = accent}
                  onMouseLeave={e => e.currentTarget.style.borderColor = isDone ? (isDark ? "#14301A" : "#B7D9A8") : border}
                >
                  {/* icon box */}
                  <div style={{
                    flexShrink: 0, width: "38px", height: "38px", borderRadius: "10px",
                    background: isDone
                      ? (isDark ? "rgba(34,197,94,0.15)" : "rgba(34,197,94,0.1)")
                      : (isDark ? "#0f1f13" : "#e8f5e8"),
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <Icon size={17} color={isDone ? accent : (isDark ? "#3a7040" : "#9abf9a")} />
                  </div>

                  {/* text */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                      margin: "0 0 2px", fontSize: "14px", fontWeight: 700,
                      color: isDone ? (isDark ? "#B7D9A8" : "#14301A") : textMain,
                    }}>
                      {l.title}
                    </p>
                    <p style={{ margin: 0, fontSize: "11.5px", color: textSub }}>{l.duration}</p>
                  </div>

                  {/* chip + done indicator */}
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
                    {chip}
                    {isDone
                      ? <CheckCircle2 size={19} color={accent} />
                      : <Circle size={19} color={isDark ? "#2a3d2e" : "#cfe5cf"} />
                    }
                  </div>
                </div>
              );
            })}
          </div>
          <SourcesPanel moduleId="breed-selection" isDark={isDark} />
        </main>
      </div>
    );
  }

  /* ───────────────────────────────────────────────────────
     VIEW: BREEDS — images now size to their natural aspect
     ratio (object-fit: contain) instead of being force-cropped
     to a fixed 180px height.
     ─────────────────────────────────────────────────────── */
  if (view === "breeds") {
    return (
      <div style={{ minHeight: "100vh", background: bg, fontFamily: "'Inter','DM Sans',system-ui,sans-serif", paddingTop: "80px" }}>
        <style>{css}</style>
        <Header title="Meet the Breeds" onBackClick={() => setView("home")} />
        <main style={{ maxWidth: "860px", margin: "0 auto", padding: isMobile ? "0 18px 90px" : "0 32px 90px" }}>
          <p className="bs-fade" style={{ fontSize: "14px", color: textSub, lineHeight: 1.7, marginBottom: "22px", maxWidth: "560px" }}>
            No single breed is "the best" — each is a tool for a specific job: volume, size, survival, or balance. Get to know all four before you decide.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "16px" }}>
            {BREEDS.map((b, i) => (
              <div key={b.name} className="bs-fade" style={{
                animationDelay: `${i * 0.06}s`,
                background: cardBg, border: `1px solid ${border}`,
                borderRadius: "16px", overflow: "hidden",
              }}>
                <div
                  className="bs-breed-img-wrap"
                  style={{ background: isDark ? "#0f1f13" : "#eef7ee" }}
                >
                  <img className="bs-breed-img" src={b.img} alt={b.name} loading="lazy" />
                </div>
                <div style={{ padding: "16px 18px" }}>
                  <span style={{
                    fontSize: "10px", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase",
                    color: accent, background: isDark ? "rgba(34,197,94,0.12)" : "rgba(34,197,94,0.1)",
                    padding: "3px 10px", borderRadius: "50px",
                  }}>
                    {b.tag}
                  </span>
                  <h3 style={{ margin: "10px 0 6px", fontFamily: "Georgia, 'Times New Roman', serif", fontSize: "19px", fontWeight: 700, color: textMain }}>
                    {b.name}
                  </h3>
                  <p style={{ margin: "0 0 10px", fontSize: "13px", color: textSub, lineHeight: 1.6 }}>{b.blurb}</p>
                  <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "7px" }}>
                    {b.bullets.map((bul, j) => (
                      <li key={j} style={{ display: "flex", gap: "8px", fontSize: "12.5px", color: isDark ? "#cfe6d4" : "#33473a", lineHeight: 1.55 }}>
                        <span style={{ color: accent, flexShrink: 0, marginTop: "1px" }}>•</span>{bul}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: "32px", display: "flex", justifyContent: "flex-end" }}>
            <NextButton />
          </div>
        </main>
      </div>
    );
  }

  /* ───────────────────────────────────────────────────────
     VIEW: STANDARD LESSON — field-notebook style
     ─────────────────────────────────────────────────────── */
  if (LESSONS[view]) {
    const lesson = LESSONS[view];
    return (
      <div style={{ minHeight: "100vh", background: bg, fontFamily: "'Inter','DM Sans',system-ui,sans-serif", paddingTop: "80px" }}>
        <style>{css}</style>
        <Header title={lesson.title} onBackClick={() => setView("home")} />

        <main data-mbz-lesson="1" style={{ maxWidth: "680px", margin: "0 auto", padding: isMobile ? "0 18px 90px" : "0 32px 90px" }}>

          {/* Title block */}
          <div className="bs-fade" style={{ marginBottom: "28px" }}>
            <h1 style={{
              margin: "0 0 12px",
              fontFamily: "Georgia, 'Times New Roman', serif",
              fontSize: isMobile ? "24px" : "30px",
              fontWeight: 700, color: textMain, lineHeight: 1.2, letterSpacing: "-0.02em",
            }}>
              {lesson.title}
            </h1>

            {/* Intro — treated as a pull-quote / lead paragraph */}
            <p style={{
              margin: 0,
              fontSize: isMobile ? "15px" : "16px",
              lineHeight: 1.75,
              color: isDark ? "#9fd4a8" : "#3a6b40",
              fontFamily: "Georgia, 'Times New Roman', serif",
              fontStyle: "italic",
              paddingLeft: "16px",
              borderLeft: `3px solid ${isDark ? "#14301A" : "#B7D9A8"}`,
            }}>
              {lesson.intro}
            </p>
          </div>

          {/* Divider */}
          <div style={{ height: "1px", background: isDark ? "#1e3321" : "#d8edd8", margin: "0 0 28px" }} />

          {/* Field-notebook insight entries */}
          <div className="bs-fade" style={{ paddingLeft: "4px" }}>
            {lesson.items.map((item, i) => (
              <InsightBlock
                key={i}
                item={item}
                isDark={isDark}
                index={i === lesson.items.length - 1 ? -1 : i}
              />
            ))}
          </div>

          {lesson.sources && (
            <div style={{ marginTop: "28px", padding: "14px 16px", borderRadius: "12px", background: isDark ? "#0f2014" : "#eef6ea", fontSize: "12.5px", lineHeight: 1.6, color: textSub }}>
              <b style={{ color: textMain }}>Sources:</b> {lesson.sources.join("; ")}.
            </div>
          )}

          <div style={{ marginTop: "36px", display: "flex", justifyContent: "flex-end" }}>
            <NextButton />
          </div>
        </main>
      </div>
    );
  }

  /* ───────────────────────────────────────────────────────
     VIEW: VIDEO
     ─────────────────────────────────────────────────────── */
  if (view === "video") {
    return (
      <div style={{ minHeight: "100vh", background: bg, fontFamily: "'Inter','DM Sans',system-ui,sans-serif", paddingTop: "80px" }}>
        <style>{css}</style>
        <Header title="Full Explainer Video" onBackClick={() => setView("home")} />
        <main style={{ maxWidth: "860px", margin: "0 auto", padding: isMobile ? "0 18px 90px" : "0 32px 90px" }}>
          <h1 className="bs-fade" style={{
            margin: "0 0 8px",
            fontFamily: "Georgia, 'Times New Roman', serif",
            fontSize: isMobile ? "24px" : "30px",
            fontWeight: 700, color: textMain, lineHeight: 1.2,
          }}>
            Kenya Dairy Goats — Full Explainer
          </h1>
          <p className="bs-fade" style={{ margin: "0 0 20px", fontSize: "14px", color: textSub, lineHeight: 1.7, maxWidth: "560px" }}>
            A walkthrough covering breeds, lactation, and management in the Kenyan context. Come back anytime — there's no need to watch it before continuing.
          </p>
          <div className="bs-fade" style={{ borderRadius: "16px", overflow: "hidden", border: `1px solid ${border}`, background: "#000" }}>
            <video controls preload="metadata" poster={VIDEO_POSTER}
              style={{ width: "100%", display: "block", maxHeight: "480px", background: "#000" }}>
              <source src={VIDEO_URL} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
          <div style={{ marginTop: "28px", display: "flex", justifyContent: "flex-end" }}>
            <NextButton />
          </div>
        </main>
      </div>
    );
  }

  /* ───────────────────────────────────────────────────────
     VIEW: QUIZ — answers persist to Firestore
     ─────────────────────────────────────────────────────── */
  if (view === "quiz") {
    return (
      <div style={{ minHeight: "100vh", background: bg, fontFamily: "'Inter','DM Sans',system-ui,sans-serif", paddingTop: "80px" }}>
        <style>{css}</style>
        <Header title="Check Your Understanding" onBackClick={() => setView("home")} />
        <main style={{ maxWidth: "740px", margin: "0 auto", padding: isMobile ? "0 18px 90px" : "0 32px 90px" }}>

          {/* Header */}
          <div className="bs-fade" style={{ marginBottom: "24px" }}>
            <h1 style={{
              margin: "0 0 8px",
              fontFamily: "Georgia, 'Times New Roman', serif",
              fontSize: isMobile ? "24px" : "30px",
              fontWeight: 700, color: textMain, lineHeight: 1.2,
            }}>
              Check Your Understanding
            </h1>
            <p style={{ margin: "0 0 12px", fontSize: "14px", color: textSub, lineHeight: 1.7 }}>
              No pressure — these aren't graded. Tap an answer to see why it's right or wrong. Your answers are saved automatically.
            </p>

            {/* Score pill */}
            {quizAnswered > 0 && (
              <div style={{ display: "inline-flex", alignItems: "center", gap: "10px" }}>
                <span style={{
                  fontSize: "13px", fontWeight: 700,
                  color: isDark ? "#8DBB7E" : "#285020",
                  background: isDark ? "#14301A" : "#E3EEDD",
                  padding: "4px 14px", borderRadius: "50px",
                  border: `1px solid ${isDark ? "#14301A" : "#B7D9A8"}`,
                }}>
                  {quizCorrect} / {quizAnswered} correct
                </span>
                {quizAnswered < QUIZ.length && (
                  <span style={{ fontSize: "12px", color: textSub }}>
                    {QUIZ.length - quizAnswered} left
                  </span>
                )}
                {quizAnswered === QUIZ.length && (
                  <span style={{ fontSize: "12px", color: textSub }}>All answered ✓</span>
                )}
              </div>
            )}
          </div>

          {QUIZ.map((item, qi) => {
            const chosen     = answers[qi];
            const isRevealed = !!revealed[qi];
            const isCorrect  = chosen === item.correct;

            return (
              <div key={qi} className="bs-fade" style={{
                animationDelay: `${qi * 0.025}s`,
                background: cardBg,
                border: `1px solid ${isRevealed
                  ? (isCorrect ? (isDark ? "#14301A" : "#B7D9A8") : (isDark ? "#7f1d1d" : "#fecaca"))
                  : border}`,
                borderRadius: "16px", padding: "20px 22px", marginBottom: "12px",
              }}>
                {/* Question */}
                <p style={{ margin: "0 0 14px", fontSize: "14.5px", fontWeight: 700, color: textMain, lineHeight: 1.55 }}>
                  <span style={{ color: isDark ? "#8DBB7E" : "#285020", marginRight: "6px", fontFamily: "Georgia, serif" }}>
                    {qi + 1}.
                  </span>
                  {item.q}
                </p>

                {/* Options */}
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {optionOrder(`breed:${qi}`, item.options.length).map((oi) => {
                    const opt = item.options[oi];
                    let optBorder = isDark ? "#2a3d2e" : "#e2efe2";
                    let optBg     = "transparent";
                    let textCol   = isDark ? "#c8e0cc" : "#2d3e31";
                    let icon      = null;

                    if (isRevealed) {
                      if (oi === item.correct) {
                        optBorder = accent;
                        optBg     = isDark ? "rgba(34,197,94,0.12)" : "rgba(34,197,94,0.08)";
                        textCol   = isDark ? "#B7D9A8" : "#14301A";
                        icon      = <Check size={14} color={accent} style={{ flexShrink: 0 }} />;
                      } else if (oi === chosen) {
                        optBorder = "#ef4444";
                        optBg     = isDark ? "rgba(239,68,68,0.1)" : "rgba(239,68,68,0.06)";
                        textCol   = isDark ? "#fca5a5" : "#dc2626";
                        icon      = <X size={14} color="#ef4444" style={{ flexShrink: 0 }} />;
                      }
                    }

                    return (
                      <div
                        key={oi}
                        className="bs-option"
                        onClick={() => choose(qi, oi)}
                        style={{
                          display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px",
                          border: `1.5px solid ${optBorder}`, background: optBg,
                          borderRadius: "10px", padding: "11px 14px",
                          fontSize: "13.5px", color: textCol,
                          cursor: isRevealed ? "default" : "pointer",
                          lineHeight: 1.5,
                          userSelect: "none",
                        }}
                      >
                        <span>{opt}</span>
                        {icon}
                      </div>
                    );
                  })}
                </div>

                {/* Why explanation */}
                {isRevealed && (
                  <div className="bs-fade" style={{
                    marginTop: "12px",
                    padding: "12px 16px",
                    borderRadius: "10px",
                    background: isDark ? "#0f1f13" : "#F1F5EE",
                    border: `1px solid ${isDark ? "#1e3321" : "#D5E8CB"}`,
                    display: "flex", gap: "10px", alignItems: "flex-start",
                  }}>
                    <div style={{
                      flexShrink: 0, width: "20px", height: "20px", borderRadius: "50%",
                      background: isCorrect ? (isDark ? "#14301A" : "#E3EEDD") : (isDark ? "#7f1d1d" : "#fee2e2"),
                      display: "flex", alignItems: "center", justifyContent: "center", marginTop: "1px",
                    }}>
                      {isCorrect
                        ? <Check size={11} color={isDark ? "#8DBB7E" : "#285020"} />
                        : <X size={11} color={isDark ? "#fca5a5" : "#dc2626"} />
                      }
                    </div>
                    <p style={{ margin: 0, fontSize: "13px", lineHeight: 1.65, color: isDark ? "#9fd4a8" : "#3a6b40" }}>
                      <strong style={{ color: textMain, fontWeight: 700 }}>Why: </strong>
                      {item.why}
                    </p>
                  </div>
                )}
              </div>
            );
          })}

          <div style={{ marginTop: "28px", display: "flex", justifyContent: "flex-end" }}>
            <NextButton />
          </div>
        </main>
      </div>
    );
  }

  /* ───────────────────────────────────────────────────────
     VIEW: JOURNAL — autosaves to Firestore
     ─────────────────────────────────────────────────────── */
  if (view === "journal") {
    const statusLabel = { idle: "Not yet saved", saving: "Saving…", saved: "Saved", error: "Save failed — retry" }[journalSave];
    const statusColor = journalSave === "saved" ? accent : journalSave === "error" ? "#ef4444" : textSub;
    const wordCount   = journal.trim().split(/\s+/).filter(Boolean).length;

    return (
      <div style={{ minHeight: "100vh", background: bg, fontFamily: "'Inter','DM Sans',system-ui,sans-serif", paddingTop: "80px" }}>
        <style>{css}</style>
        <Header title="Your Breed Selection Plan" onBackClick={() => setView("home")} />
        <main style={{ maxWidth: "680px", margin: "0 auto", padding: isMobile ? "0 18px 90px" : "0 32px 90px" }}>

          <h1 className="bs-fade" style={{
            margin: "0 0 8px",
            fontFamily: "Georgia, 'Times New Roman', serif",
            fontSize: isMobile ? "24px" : "30px",
            fontWeight: 700, color: textMain, lineHeight: 1.2,
          }}>
            Write Your Breed Selection Plan
          </h1>

          <p className="bs-fade" style={{ margin: "0 0 8px", fontSize: "14px", color: textSub, lineHeight: 1.7, maxWidth: "540px" }}>
            Based on everything in this topic, write a short plan. Cover:
          </p>

          {/* Prompt bullets — subtle, not noisy */}
          <ul className="bs-fade" style={{
            margin: "0 0 24px", padding: 0, listStyle: "none",
            display: "flex", flexDirection: "column", gap: "6px",
          }}>
            {[
              "Which breed(s) you'd choose for your land and climate",
              "What age you'd buy at, and why",
              "What genetic level (% crossbred) you'd target",
              "One risk you're most worried about managing",
            ].map((pt, i) => (
              <li key={i} style={{ display: "flex", gap: "10px", fontSize: "13.5px", color: isDark ? "#7fa183" : "#5a7a5d", lineHeight: 1.55 }}>
                <span style={{ color: accent, flexShrink: 0, fontWeight: 800 }}>→</span>{pt}
              </li>
            ))}
          </ul>

          {/* Text area */}
          <div className="bs-fade" style={{
            background: cardBg,
            border: `1.5px solid ${journalSave === "error" ? "#ef4444" : isDark ? "#223328" : "#c8e0c8"}`,
            borderRadius: "14px", padding: "4px",
            transition: "border-color 0.15s",
          }}>
            <textarea
              value={journal}
              onChange={e => handleJournalChange(e.target.value)}
              placeholder="Start writing… e.g. 'I farm in a semi-arid area near Marsabit, so I'd start with a 75% German Alpine crossbred…'"
              style={{
                width: "100%", minHeight: "240px", resize: "vertical",
                border: "none", outline: "none", background: "transparent",
                color: textMain, fontFamily: "Georgia, 'Times New Roman', serif",
                fontSize: "15px", lineHeight: 1.75,
                padding: "14px 16px", boxSizing: "border-box",
              }}
            />
          </div>

          {/* Status row */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "10px" }}>
            <span style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: statusColor, fontWeight: 600 }}>
              {journalSave === "saving"
                ? <Loader2 size={12} color={textSub} style={{ animation: "spin 1s linear infinite" }} />
                : <Save size={12} />
              }
              {statusLabel}
            </span>
            <span style={{ fontSize: "12px", color: textSub }}>{wordCount} {wordCount === 1 ? "word" : "words"}</span>
          </div>

          {/* Note */}
          <div style={{
            marginTop: "20px", padding: "14px 18px", borderRadius: "12px",
            background: isDark ? "#0f1f13" : "#F1F5EE",
            border: `1px solid ${isDark ? "#1e3321" : "#D5E8CB"}`,
          }}>
            <p style={{ margin: 0, fontSize: "12.5px", color: textSub, lineHeight: 1.65 }}>
              Saved to your account — accessible from any device. Come back and edit it anytime as you work through the rest of the course.
            </p>
          </div>

          <div style={{ marginTop: "28px", display: "flex", justifyContent: "flex-end" }}>
            <NextButton label="Back to course" />
          </div>
        </main>
      </div>
    );
  }

  return null;
}